import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { type HourDetail, optimizerApi } from "@/api/optimizer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { todayIso } from "@/lib/format";
import { cn } from "@/lib/utils";

type Tab = "planning" | "retro";

export function OptimizerPage() {
  const { t } = useTranslation();
  const [date, setDate] = useState(todayIso());
  const [tab, setTab] = useState<Tab>("planning");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-brand-navy-700">
          {t("optimizer.title")}
        </h1>
        <p className="text-sm text-slate-500">{t("optimizer.subtitle")}</p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center gap-3 flex-wrap">
          <Label className="text-sm">{t("optimizer.date")}:</Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-44"
          />
          <div className="flex border-b border-slate-200 ml-auto">
            <TabButton active={tab === "planning"} onClick={() => setTab("planning")}>
              {t("optimizer.tab_planning")}
            </TabButton>
            <TabButton active={tab === "retro"} onClick={() => setTab("retro")}>
              {t("optimizer.tab_retro_intraday")}
            </TabButton>
          </div>
        </CardHeader>
      </Card>

      {tab === "planning" ? (
        <PlanningView date={date} />
      ) : (
        <RetroIntradayView date={date} />
      )}
    </div>
  );
}

function TabButton({
  active, onClick, children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-4 py-2 text-sm font-medium border-b-2 transition-colors",
        active
          ? "border-brand-navy-500 text-brand-navy-700"
          : "border-transparent text-slate-500 hover:text-slate-700",
      )}
    >
      {children}
    </button>
  );
}

// ---------- Planning view ----------

function PlanningView({ date }: { date: string }) {
  const { t } = useTranslation();
  const dayQ = useQuery({
    queryKey: ["optimizer", "day", date],
    queryFn: () => optimizerApi.day(date),
    retry: 0,
  });

  if (dayQ.isLoading) {
    return (
      <div className="flex items-center gap-2 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
      </div>
    );
  }
  if (dayQ.isError || !dayQ.data) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-amber-700">
          {t("optimizer.rdn_not_published")}
        </CardContent>
      </Card>
    );
  }

  const total = dayQ.data.total_optimal_profit_uah;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            {t("optimizer.total_optimal")}:{" "}
            <span className="text-brand-mint-600">
              {Math.round(total).toLocaleString("uk-UA")} грн
            </span>
          </CardTitle>
        </CardHeader>
      </Card>

      {dayQ.data.rdn_is_forecast && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {t("optimizer.rdn_price_forecast_hint")}
        </div>
      )}
      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 bg-slate-50">
                <th className="p-2 w-14">{t("common.hour")}</th>
                <th className="p-2">{t("optimizer.rdn_price")}</th>
                <th className="p-2 text-right">{t("optimizer.plan_ekosfera")}</th>
                <th className="p-2 text-right">{t("optimizer.best_power")}</th>
                <th className="p-2 text-right">{t("optimizer.volume_ekosfera")}</th>
                <th className="p-2 text-right">{t("optimizer.volume_rdn")}</th>
                <th className="p-2 text-right">{t("optimizer.best_profit")}</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {dayQ.data.hours.map((h) => (
                <HourRow key={h.hour} hour={h.hour} date={date} item={h} />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </>
  );
}

// ---------- Hour row with collapsible chart ----------

import type { HourOptimum } from "@/api/optimizer";

interface HourRowProps {
  hour: number;
  date: string;
  item: HourOptimum;
}

function HourRow({ hour, date, item }: HourRowProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const detailQ = useQuery({
    queryKey: ["optimizer", "hour", date, hour],
    queryFn: () => optimizerApi.hour(date, hour),
    enabled: open,
    retry: 0,
  });

  const rdnPrice = item.rdn_price_uah_per_mwh;
  const planEko = item.plan_ekosfera_kwh;
  const bestPower = item.best.power_kw;
  const bestProfit = item.best.profit_uah;
  const volEko = item.best.volumes_kwh.ekosfera ?? 0;
  const volRdn = item.best.volumes_kwh.rdn ?? 0;
  const deficit = item.best.ekosfera_deficit_kwh;

  return (
    <>
      <tr
        className={
          "border-t border-slate-100 hover:bg-slate-50/40 " +
          (deficit > 0 ? "bg-red-50/60" : "")
        }
      >
        <td className="p-2 font-mono text-xs">{hour.toString().padStart(2, "0")}:00</td>
        <td className="p-2">
          {rdnPrice !== null ? (
            <span className="inline-flex items-center gap-2">
              {rdnPrice.toLocaleString("uk-UA")}
              {item.rdn_price_is_forecast && (
                <span
                  title={t("optimizer.rdn_price_forecast_hint")}
                  className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase text-amber-700"
                >
                  {t("optimizer.rdn_price_forecast_badge")}
                </span>
              )}
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </td>
        <td className="p-2 text-right font-mono">
          {planEko !== null && planEko > 0 ? Math.round(planEko).toLocaleString("uk-UA") : "—"}
        </td>
        <td className="p-2 text-right font-medium">{Math.round(bestPower)}</td>
        <td className="p-2 text-right font-mono">
          <span className="inline-flex items-center gap-2">
            {Math.round(volEko).toLocaleString("uk-UA")}
            {deficit > 0 && (
              <span
                title={t("optimizer.deficit_hint")}
                className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700"
              >
                {t("optimizer.deficit_warning", { kwh: Math.round(deficit) })}
              </span>
            )}
          </span>
        </td>
        <td className="p-2 text-right font-mono">{Math.round(volRdn).toLocaleString("uk-UA")}</td>
        <td className="p-2 text-right">
          <span className={bestProfit >= 0 ? "text-brand-mint-700" : "text-red-700"}>
            {bestProfit >= 0 ? "+" : ""}
            {Math.round(bestProfit).toLocaleString("uk-UA")}
          </span>
        </td>
        <td className="p-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen((v) => !v)}
            className="text-xs"
          >
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {open ? t("optimizer.hide_chart") : t("optimizer.show_chart")}
          </Button>
        </td>
      </tr>
      {open && (
        <tr className="bg-slate-50/30 border-b border-slate-100">
          <td colSpan={8} className="p-4">
            {detailQ.isLoading ? (
              <div className="text-slate-500 text-xs flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> {t("common.loading")}
              </div>
            ) : detailQ.data ? (
              <HourCharts detail={detailQ.data} />
            ) : (
              <span className="text-amber-700 text-sm">{t("optimizer.rdn_not_published")}</span>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ---------- Charts ----------

function HourCharts({ detail }: { detail: HourDetail }) {
  const { t } = useTranslation();

  // Перетворюємо сценарії у точки для графіка.
  const points = detail.scenarios.map((s) => ({
    power_kw: s.power_kw,
    profit_uah: s.profit_uah,
    cost_per_mwh: s.cost_uah_per_mwh,
  }));

  const bestPoint = detail.scenarios[detail.best_index];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="h-64 bg-white rounded-md border border-slate-200 p-2">
        <div className="text-xs text-slate-500 mb-1">{t("optimizer.chart_title_profit")}</div>
        <ResponsiveContainer width="100%" height="90%">
          <LineChart data={points} margin={{ top: 10, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid stroke="#e2e8f0" />
            <XAxis
              dataKey="power_kw"
              type="number"
              domain={["dataMin", "dataMax"]}
              label={{ value: t("optimizer.axis_power"), position: "insideBottomRight", offset: -4, fill: "#64748b", fontSize: 11 }}
            />
            <YAxis
              width={75}
              domain={[
                (dataMin: number) => Math.floor(dataMin - Math.abs(dataMin) * 0.05),
                (dataMax: number) => Math.ceil(dataMax + Math.abs(dataMax) * 0.05),
              ]}
              label={{ value: t("optimizer.axis_profit"), angle: -90, position: "insideLeft", fill: "#64748b", fontSize: 11 }}
            />
            <Tooltip />
            <Line dataKey="profit_uah" stroke="#3ec79a" strokeWidth={2} dot={false} />
            <ReferenceDot
              x={bestPoint.power_kw}
              y={bestPoint.profit_uah}
              r={6}
              fill="#002060"
              stroke="#fff"
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="h-64 bg-white rounded-md border border-slate-200 p-2">
        <div className="text-xs text-slate-500 mb-1">{t("optimizer.chart_title_cost")}</div>
        <ResponsiveContainer width="100%" height="90%">
          <LineChart data={points} margin={{ top: 10, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid stroke="#e2e8f0" />
            <XAxis
              dataKey="power_kw"
              type="number"
              domain={["dataMin", "dataMax"]}
              label={{ value: t("optimizer.axis_power"), position: "insideBottomRight", offset: -4, fill: "#64748b", fontSize: 11 }}
            />
            <YAxis
              width={75}
              domain={[
                (dataMin: number) => Math.floor(dataMin * 0.95),
                (dataMax: number) => Math.ceil(dataMax * 1.05),
              ]}
              label={{ value: t("optimizer.axis_cost"), angle: -90, position: "insideLeft", fill: "#64748b", fontSize: 11 }}
            />
            <Tooltip />
            <Line dataKey="cost_per_mwh" stroke="#002060" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ---------- Retro Intraday view ----------

function RetroIntradayView({ date }: { date: string }) {
  const { t } = useTranslation();
  const retroQ = useQuery({
    queryKey: ["optimizer", "retro-intraday", date],
    queryFn: () => optimizerApi.retroIntraday(date),
    retry: 0,
  });

  if (retroQ.isLoading) {
    return (
      <div className="flex items-center gap-2 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
      </div>
    );
  }
  if (retroQ.isError || !retroQ.data) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-amber-700">
          {t("optimizer.rdn_not_published")}
        </CardContent>
      </Card>
    );
  }

  const r = retroQ.data;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-slate-500">
              {t("optimizer.total_optimal")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold text-brand-mint-600">
              {Math.round(r.optimal_profit_uah).toLocaleString("uk-UA")} грн
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-slate-500">
              {t("optimizer.submitted_profit")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold text-brand-navy-700">
              {r.submitted_plan_profit_uah !== null
                ? `${Math.round(r.submitted_plan_profit_uah).toLocaleString("uk-UA")} грн`
                : <span className="text-slate-400 text-base">—</span>}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-slate-500">
              {t("optimizer.lost_potential")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold text-amber-600">
              {r.lost_potential_uah !== null
                ? `−${Math.round(r.lost_potential_uah).toLocaleString("uk-UA")} грн`
                : <span className="text-slate-400 text-base">—</span>}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 bg-slate-50">
                <th className="p-2 w-14">{t("common.hour")}</th>
                <th className="p-2">{t("optimizer.rdn_price")}</th>
                <th className="p-2 text-right">{t("optimizer.plan_ekosfera")}</th>
                <th className="p-2 text-right">{t("optimizer.best_power")}</th>
                <th className="p-2 text-right">{t("optimizer.volume_ekosfera")}</th>
                <th className="p-2 text-right">{t("optimizer.volume_rdn")}</th>
                <th className="p-2 text-right">{t("optimizer.best_profit")}</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {r.hours.map((h) => (
                <HourRow key={h.hour} hour={h.hour} date={date} item={h} />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </>
  );
}
